import { zodResolver } from "@hookform/resolvers/zod";
import { CaretDownIcon } from "@radix-ui/react-icons";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { CheckIcon, Info, SquarePen, StarIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { type DateRange } from "react-day-picker";
import { useForm } from "react-hook-form";
import { useDebounce, useLocalStorage } from "react-use";
import { type z } from "zod";
import { BreedsPicker } from "./BreedsPicker";
import { DestinationCityPicker } from "./DestinationCityPicker";
import { DestinationPicker } from "./DestinationPicker";
import { DestinationStatePicker } from "./DestinationStatePicker";
import { FeedyardPicker } from "./FeedyardPicker";
import { SexPicker } from "./SexPicker";
import { SourceCityPicker } from "./SourceCityPicker";
import { SourcePicker } from "./SourcePicker";
import { SourceStatePicker } from "./SourceStatePicker";
import { createBenchmarkFormSchema } from "./validation";
import { FilterBadges } from "@/benchmarks/BenchmarkFilters/FilterBadges";
import { DateRangePicker } from "@/components/DateRangePicker/index";
import { FilterSheet } from "@/components/FilterSheet";
import { type PaginationProperties } from "@/components/Pagination";
import { TableFilters } from "@/components/Table/TableFilters";
import { Heading3, Paragraph } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WeightRangeInput } from "@/components/WeightRangeInput";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFilterSheet } from "@/hooks/useFilterSheet";
import { cn } from "@/lib/utils/common";
import { toIsoDate, toJsDate } from "@/lib/utils/dates";
import { filterSchema, type BenchmarkFilters } from "@/routes/(authed)/_layout.benchmarks";

const routeApi = getRouteApi("/(authed)/_layout/benchmarks");

export type SavedBenchmarks = Record<string, ReturnType<typeof routeApi.useSearch>>;
type FormSchema = z.infer<ReturnType<typeof createBenchmarkFormSchema>>;

export function BenchmarkFilters({
  handleResetPagination,
}: {
  handleResetPagination: PaginationProperties["controls"]["handleResetPagination"];
}) {
  const navigate = useNavigate({ from: "/benchmarks" });
  const searchParams = routeApi.useSearch();
  const isMobile = useIsMobile();
  const [benchmarks, setBenchmarks] = useLocalStorage<SavedBenchmarks>("benchmarkFilters", {});
  const [selectedBenchmarkName, setSelectedBenchmarkName] = useState<string>("");
  const [filters, setFilters] = useState<BenchmarkFilters>(searchParams);
  const [inactiveFilters, setInactiveFilters] = useState<BenchmarkFilters>({});

  useDebounce(
    () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, values]) => (Array.isArray(values) ? values.length > 0 : !!values)),
      );

      handleResetPagination();

      void navigate({ search: cleanFilters });
    },
    300,
    [filters],
  );

  const { filterButton, filterSheetProps, localFilters, setLocalFilters } = useFilterSheet({
    initialFilters: filters,
    onApply: (newFilters) => {
      setSelectedBenchmarkName("");
      setFilters(newFilters);
      // The sheet is the authoritative statement of the filter set, so applying
      // or clearing it discards any inactive values left over from the badges.
      setInactiveFilters({});
    },
    getActiveCount: countActiveFilters,
  });

  const hasActiveFilters = hasFilterValues(filters) || hasFilterValues(inactiveFilters);

  const handleFilterChange = (patch: BenchmarkFilters): void => {
    setSelectedBenchmarkName("");
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const handleClearFilters = () => {
    setSelectedBenchmarkName("");
    setFilters({});
    setInactiveFilters({});
  };

  const clearFiltersButton = hasActiveFilters ? (
    <Button type="button" variant="outline" className="rounded-3xl border-none" onClick={handleClearFilters}>
      <XIcon className="mr-2 h-4 w-4" />
      Clear Filters
    </Button>
  ) : null;

  return (
    <section data-testid="benchmark-filters" className="border-b border-border bg-secondary p-4">
      <div className="flex justify-between flex-wrap">
        <div>
          {isMobile ? (
            <div className="mb-2 flex items-center gap-1">
              <Heading3 className="text-base">Benchmark Filters</Heading3>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" aria-label="About benchmark filters">
                    <Info className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 text-sm">
                  {filtersDescription}
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <>
              <Heading3 className="text-base">Benchmark Filters</Heading3>
              <Paragraph>{filtersDescription}</Paragraph>
            </>
          )}
        </div>
        <div className="flex items-center mb-2">
          <SaveBenchmarkDialog
            benchmarks={benchmarks}
            setBenchmarks={setBenchmarks}
            selectedBenchmarkName={selectedBenchmarkName}
            setSelectedBenchmarkName={setSelectedBenchmarkName}
            filters={filters}
          />

          <EditBenchmarkDialog
            benchmarks={benchmarks}
            setBenchmarks={setBenchmarks}
            selectedBenchmarkName={selectedBenchmarkName}
            setSelectedBenchmarkName={setSelectedBenchmarkName}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
      </div>
      {isMobile ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <TableFilters {...filterButton} />
            {clearFiltersButton}
          </div>
          <FilterSheet {...filterSheetProps}>
            <BenchmarkFilterFields
              filters={localFilters}
              onChange={(patch) => setLocalFilters((prev) => ({ ...prev, ...patch }))}
              className="flex flex-col gap-4"
              fieldClassName="w-full"
            />
          </FilterSheet>
        </>
      ) : (
        <BenchmarkFilterFields
          filters={filters}
          onChange={handleFilterChange}
          className="flex flex-wrap gap-4"
          fieldClassName="w-[220px]"
        >
          {clearFiltersButton ? <div className="flex items-end">{clearFiltersButton}</div> : null}
        </BenchmarkFilterFields>
      )}
      <div className="min-h-[42px] pt-4">
        <FilterBadges
          filters={filters}
          setFilters={setFilters}
          setSelectedBenchmarkName={setSelectedBenchmarkName}
          inactiveFilters={inactiveFilters}
          setInactiveFilters={setInactiveFilters}
        />
      </div>
    </section>
  );
}

const filtersDescription = "Use filters to adjust the table's weighted averages for a customized benchmark view.";

function BenchmarkFilterFields({
  filters,
  onChange,
  className,
  fieldClassName,
  children,
}: {
  filters: BenchmarkFilters;
  onChange: (patch: BenchmarkFilters) => void;
  className: string;
  fieldClassName: string;
  children?: React.ReactNode;
}): React.JSX.Element {
  const fieldWrapperClassName = cn("flex flex-col gap-2", fieldClassName);

  const handleDateRangeChange =
    (
      after: "dateOpenedAfter" | "dateClosedAfter",
      before: "dateOpenedBefore" | "dateClosedBefore",
    ): ((range: DateRange | undefined) => void) =>
    (range: DateRange | undefined): void =>
      onChange({ [after]: toIsoDate(range?.from), [before]: toIsoDate(range?.to) });

  return (
    <div className={className}>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-lot-id">Lot ID</Label>
        <Input
          id="benchmark-filter-lot-id"
          onChange={(e) => onChange({ lotId: e.target.value || undefined })}
          value={filters.lotId || ""}
          placeholder="Search by Lot ID"
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-feedyard">Feedyard</Label>
        <FeedyardPicker
          id="benchmark-filter-feedyard"
          onSelect={(values) => onChange({ feedyardIds: emptyToUndefined(values) })}
          selected={filters.feedyardIds || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-sex">Sex</Label>
        <SexPicker
          id="benchmark-filter-sex"
          onSelect={(values) => onChange({ sexClass: emptyToUndefined(values) })}
          selected={filters.sexClass || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-breeds">Breeds</Label>
        <BreedsPicker
          id="benchmark-filter-breeds"
          onSelect={(values) => onChange({ animalBreedIds: emptyToUndefined(values) })}
          selected={filters.animalBreedIds || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-source">Source</Label>
        <SourcePicker
          id="benchmark-filter-source"
          onSelect={(values) => onChange({ lotSourceIds: emptyToUndefined(values) })}
          selected={filters.lotSourceIds || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-source-city">Source City</Label>
        <SourceCityPicker
          id="benchmark-filter-source-city"
          onSelect={(values) => onChange({ sourceCities: emptyToUndefined(values) })}
          selected={filters.sourceCities || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-source-state">Source State</Label>
        <SourceStatePicker
          id="benchmark-filter-source-state"
          onSelect={(values) => onChange({ sourceStateCodes: emptyToUndefined(values) })}
          selected={filters.sourceStateCodes || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-destination">Destination</Label>
        <DestinationPicker
          id="benchmark-filter-destination"
          onSelect={(values) => onChange({ lotDestinationIds: emptyToUndefined(values) })}
          selected={filters.lotDestinationIds || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-destination-city">Destination City</Label>
        <DestinationCityPicker
          id="benchmark-filter-destination-city"
          onSelect={(values) => onChange({ destinationCities: emptyToUndefined(values) })}
          selected={filters.destinationCities || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-destination-state">Destination State</Label>
        <DestinationStatePicker
          id="benchmark-filter-destination-state"
          onSelect={(values) => onChange({ destinationStateCodes: emptyToUndefined(values) })}
          selected={filters.destinationStateCodes || []}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-date-opened">Date Opened</Label>
        <DateRangePicker
          id="benchmark-filter-date-opened"
          onChange={handleDateRangeChange("dateOpenedAfter", "dateOpenedBefore")}
          dateRange={{
            from: toJsDate(filters.dateOpenedAfter),
            to: toJsDate(filters.dateOpenedBefore),
          }}
        />
      </div>
      <div className={fieldWrapperClassName}>
        <Label htmlFor="benchmark-filter-date-closed">Date Closed</Label>
        <DateRangePicker
          id="benchmark-filter-date-closed"
          onChange={handleDateRangeChange("dateClosedAfter", "dateClosedBefore")}
          dateRange={{
            from: toJsDate(filters.dateClosedAfter),
            to: toJsDate(filters.dateClosedBefore),
          }}
        />
      </div>
      <WeightRangeInput
        onWeightChange={(min, max) => onChange({ weightAverageInMin: min, weightAverageInMax: max })}
        minWeight={filters.weightAverageInMin}
        maxWeight={filters.weightAverageInMax}
        step="50"
        className={fieldClassName}
      />
      {children}
    </div>
  );
}

const emptyToUndefined = <T,>(values: Array<T> | undefined): Array<T> | undefined =>
  values?.length ? values : undefined;

function BenchmarkDropdownMenu({
  benchmarks,
  setSelectedBenchmarkName,
  selectedBenchmarkName,
  setFilters,
}: {
  benchmarks: SavedBenchmarks | undefined;
  setSelectedBenchmarkName: React.Dispatch<React.SetStateAction<string>>;
  selectedBenchmarkName: string;
  setFilters: React.Dispatch<React.SetStateAction<BenchmarkFilters>>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          aria-label="Load benchmark"
          variant="outline"
          className={"rounded-l-none border-l-0 px-2"}
        >
          <CaretDownIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[200px]">
        {Object.entries(benchmarks || {}).length === 0 ? (
          <DropdownMenuItem disabled>
            <span className="text-muted-foreground">No saved benchmarks</span>
          </DropdownMenuItem>
        ) : (
          Object.entries(benchmarks || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([benchmarkName, filterParams]) => (
              <DropdownMenuItem
                key={benchmarkName}
                className="flex justify-between"
                onSelect={() => {
                  const result = filterSchema.safeParse(filterParams);
                  setSelectedBenchmarkName(benchmarkName);
                  result.success ? setFilters(result.data) : setFilters({});
                }}
              >
                <span>{benchmarkName}</span>
                <div className="flex items-center gap-1">
                  {selectedBenchmarkName === benchmarkName ? <CheckIcon className="h-3 w-3" /> : null}
                  <DialogTrigger
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBenchmarkName(benchmarkName);
                      setOpen(false);
                    }}
                  >
                    <SquarePen className="h-3 w-3" />
                  </DialogTrigger>
                </div>
              </DropdownMenuItem>
            ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SaveBenchmarkDialog({
  benchmarks,
  setBenchmarks,
  selectedBenchmarkName,
  setSelectedBenchmarkName,
  filters,
}: {
  benchmarks: SavedBenchmarks | undefined;
  setBenchmarks: React.Dispatch<React.SetStateAction<SavedBenchmarks | undefined>>;
  selectedBenchmarkName: string;
  setSelectedBenchmarkName: React.Dispatch<React.SetStateAction<string>>;
  filters: BenchmarkFilters;
}) {
  const [open, setOpen] = useState(false);

  const updateBenchmarksInLocalStorage = ({ benchmarkName }: FormSchema) => {
    setBenchmarks({ ...benchmarks, [benchmarkName]: filters });
  };

  const handleSaveBenchmark = (name: string) => {
    setSelectedBenchmarkName(name);
    updateBenchmarksInLocalStorage({ benchmarkName: name });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant={"outline"} className={"rounded-r-none"}>
          {selectedBenchmarkName ? (
            <StarIcon fill="currentColor" className="mr-2 h-4 w-4" />
          ) : (
            <StarIcon className="mr-2 h-4 w-4" />
          )}
          Save Benchmark
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Benchmark</DialogTitle>
          <DialogDescription className="sr-only">Provide a name for your benchmark to save it.</DialogDescription>{" "}
        </DialogHeader>
        <BenchmarkForm onSave={handleSaveBenchmark} onCancel={() => setOpen(false)} benchmarks={benchmarks} />
      </DialogContent>
    </Dialog>
  );
}

function EditBenchmarkDialog({
  benchmarks,
  setBenchmarks,
  selectedBenchmarkName,
  setSelectedBenchmarkName,
  filters,
  setFilters,
}: {
  benchmarks: SavedBenchmarks | undefined;
  setBenchmarks: React.Dispatch<React.SetStateAction<SavedBenchmarks | undefined>>;
  selectedBenchmarkName: string;
  setSelectedBenchmarkName: React.Dispatch<React.SetStateAction<string>>;
  filters: BenchmarkFilters;
  setFilters: React.Dispatch<React.SetStateAction<BenchmarkFilters>>;
}) {
  const [open, setOpen] = useState(false);

  const updateBenchmarksInLocalStorage = ({ benchmarkName }: FormSchema) => {
    if (selectedBenchmarkName && benchmarks && benchmarks[selectedBenchmarkName]) {
      const { [selectedBenchmarkName]: _, ...rest } = benchmarks;

      setBenchmarks({ ...rest, [benchmarkName]: filters });
    }
  };

  const handleDeleteBenchmark = () => {
    setSelectedBenchmarkName("");

    if (selectedBenchmarkName && benchmarks && benchmarks[selectedBenchmarkName]) {
      const { [selectedBenchmarkName]: _, ...rest } = benchmarks;
      setBenchmarks(rest);
      setOpen(false);
    }
  };

  const handleSaveBenchmark = (name: string) => {
    updateBenchmarksInLocalStorage({ benchmarkName: name });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <BenchmarkDropdownMenu
        benchmarks={benchmarks}
        setSelectedBenchmarkName={setSelectedBenchmarkName}
        selectedBenchmarkName={selectedBenchmarkName}
        setFilters={setFilters}
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Benchmark</DialogTitle>
          <DialogDescription className="sr-only">Edit the details of your benchmark.</DialogDescription>{" "}
        </DialogHeader>
        {selectedBenchmarkName ? (
          <BenchmarkForm
            initialName={selectedBenchmarkName}
            onSave={handleSaveBenchmark}
            onDelete={handleDeleteBenchmark}
            onCancel={() => {
              setOpen(false);
            }}
            benchmarks={benchmarks}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function BenchmarkForm({
  onCancel,
  onSave,
  initialName = "",
  onDelete,
  benchmarks,
}: {
  initialName?: string;
  onSave: (name: string) => void;
  onCancel: () => void;
  onDelete?: () => void;
  benchmarks: SavedBenchmarks | undefined;
}) {
  const formSchema = createBenchmarkFormSchema({ benchmarks, existingBenchmarkName: initialName });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      benchmarkName: initialName,
    },
  });

  const handleSave = form.handleSubmit((data) => {
    onSave(data.benchmarkName);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSave} className="space-y-8">
        <FormField
          control={form.control}
          name="benchmarkName"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} />
                </FormControl>
                <FormDescription>25 character limit</FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <div className="flex flex-1 justify-between">
          {onDelete ? (
            <Button
              onClick={onDelete}
              type="button"
              className="text-destructive hover:bg-background hover:text-destructive/80"
              variant={"ghost"}
            >
              Delete
            </Button>
          ) : null}
          <div className="gap-2 flex">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

const hasValue = (value: BenchmarkFilters[keyof BenchmarkFilters]): boolean =>
  Array.isArray(value) ? value.length > 0 : !!value;

export const hasFilterValues = (filters: BenchmarkFilters): boolean => Object.values(filters).some(hasValue);

// Ranges are stored as two keys but shown as one field, so each counts once.
const filterFieldKeys: Array<Array<keyof BenchmarkFilters>> = [
  ["lotId"],
  ["feedyardIds"],
  ["sexClass"],
  ["animalBreedIds"],
  ["lotSourceIds"],
  ["sourceCities"],
  ["sourceStateCodes"],
  ["lotDestinationIds"],
  ["destinationCities"],
  ["destinationStateCodes"],
  ["dateOpenedAfter", "dateOpenedBefore"],
  ["dateClosedAfter", "dateClosedBefore"],
  ["weightAverageInMin", "weightAverageInMax"],
];

export const countActiveFilters = (filters: BenchmarkFilters): number =>
  filterFieldKeys.filter((keys) => keys.some((key) => hasValue(filters[key]))).length;
